import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { buildSeoContactFromApi } from '../seo/seo-settings';
import { localBusinessJsonLd, faqPageJsonLd, servicePageJsonLd, breadcrumbJsonLd } from '../seo/json-ld';
import { getServicePageContent } from '../seo/service-pages';
import { SERVICE_SLUG_BY_PATH } from '../seo/static-routes';
import { STATIC_PAGE_COPY } from '../seo/static-page-copy';
import { getSeoForPath } from '../seo/routes';

function breadcrumbItemsForPath(pathname: string, h1: string): { name: string; path: string }[] {
  const items: { name: string; path: string }[] = [{ name: 'Accueil', path: '/' }];
  if (pathname === '/') return items;

  const slug = SERVICE_SLUG_BY_PATH[pathname];
  if (slug) {
    items.push({ name: 'Services', path: '/services' });
    items.push({ name: h1, path: pathname });
    return items;
  }

  items.push({ name: h1, path: pathname });
  return items;
}

export function SeoJsonLd() {
  const { pathname } = useLocation();
  const { settings } = useSettings();
  const seo = getSeoForPath(pathname);
  const h1 = seo.h1 ?? seo.title;

  const contact = buildSeoContactFromApi(
    (settings ?? {}) as Record<string, unknown>,
  );

  const scripts: string[] = [localBusinessJsonLd(contact)];

  const slug = SERVICE_SLUG_BY_PATH[pathname];
  const serviceContent = slug ? getServicePageContent(slug) : null;

  if (serviceContent) {
    scripts.push(servicePageJsonLd(serviceContent.h1, serviceContent.intro, pathname));
    if (serviceContent.faq.length) {
      scripts.push(faqPageJsonLd(serviceContent.faq));
    }
  } else {
    const staticCopy = STATIC_PAGE_COPY[pathname];
    if (staticCopy?.faq?.length) {
      scripts.push(faqPageJsonLd(staticCopy.faq));
    }
  }

  if (pathname !== '/') {
    scripts.push(breadcrumbJsonLd(breadcrumbItemsForPath(pathname, h1)));
  }

  return (
    <Helmet>
      {scripts.map((json) => (
        <script key={json.slice(0, 48)} type="application/ld+json">
          {json}
        </script>
      ))}
    </Helmet>
  );
}
